import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import EmailServices from '@Services/Email.services.ts';
import { WelcomeEmailFormFields } from '@Types/Form.types';
import { formatRequired } from '@Utils/form.utils';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { getUserWithLocation } from '@Utils/user.utils';
import { useApolloClient } from '@apollo/client';
import { ErrorDisplay, FreeText, Toast } from '@kg-internal/pv3-uikit';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { GetEmployeeByEmailDocument, GetEmployeeByEmailQuery } from '~/__generated__/graph';

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const { EMAIL_RULES } = VALIDATION_RULES;
const WelcomeEmailModal = ({ show, onClose, onSuccess }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    handleSubmit,
    formState: { errors },
    reset,
    register,
  } = useForm<WelcomeEmailFormFields>();
  const client = useApolloClient();

  const handleSendWelcomeEmail = async (data: WelcomeEmailFormFields) => {
    const { data: kgUser, error: portalError } = await client.query<GetEmployeeByEmailQuery>({
      query: GetEmployeeByEmailDocument,
      variables: { email: data.userEmail },
    });

    if (portalError || !kgUser.person) {
      Toast.warning('Could not fetch employee.');
    } else {
      const userWithLocation = getUserWithLocation(kgUser.person);

      EmailServices.welcomeEmail(userWithLocation)
        .then(() => {
          Toast.success('Welcome email sent successfully.');
          onSuccess?.();
          reset();
          onClose();
        })
        .catch(({ response }) => handleRequestError(response, setErrorMessage, true));
    }
  };

  return (
    <div className='modal--fit-content modal--pt-25vh modal--w-40vw'>
      <ModalWrapper
        title='Send Welcome Email'
        primaryButtonText='Confirm'
        show={show}
        handleClose={onClose}
        primaryAction={() => handleSubmit(handleSendWelcomeEmail)()}>
        {errorMessage ? (
          <ErrorDisplay inline details={errorMessage} />
        ) : (
          <form onSubmit={() => handleSubmit(handleSendWelcomeEmail)()}>
            <div className='grid'>
              <div className='col-12'>
                <p>This will send a welcome email to the user. Please confirm to proceed.</p>
                <FreeText
                  label={formatRequired('User Email')}
                  fullWidth
                  error={!!errors.userEmail}
                  errorMessage={errors.userEmail?.message}
                  {...register('userEmail', EMAIL_RULES.EMAIL)}
                />
              </div>
            </div>
          </form>
        )}
      </ModalWrapper>
    </div>
  );
};

export default WelcomeEmailModal;
